// Persistent cache for r.jina.ai reader fetches and DuckDuckGo searches so
// Sophia can re-use previously scraped pages instantly and stay useful while
// offline. Stored in its own IndexedDB so it never collides with the main
// lattice schema migrations.

import { openDB, type IDBPDatabase } from "idb";

const DB_NAME = "sophia-jina-cache";
const DB_VERSION = 1;

let dbp: Promise<IDBPDatabase> | null = null;
function db() {
  if (!dbp) {
    dbp = openDB(DB_NAME, DB_VERSION, {
      upgrade(d) {
        if (!d.objectStoreNames.contains("pages")) {
          const s = d.createObjectStore("pages", { keyPath: "key" });
          s.createIndex("fetchedAt", "fetchedAt");
          s.createIndex("kind", "kind");
        }
      },
    });
  }
  return dbp;
}

export type CacheKind = "page" | "search";

export interface CachedPage {
  /** stable cache key (url for pages, `search:<query>` for searches) */
  key: string;
  kind: CacheKind;
  url: string;
  text: string;
  bytes: number;
  fetchedAt: number;
  hits: number;
}

export function pageKey(url: string): string { return `page:${url}`; }
export function searchKey(query: string): string { return `search:${query.toLowerCase().trim()}`; }

export async function cacheGet(key: string): Promise<CachedPage | undefined> {
  try {
    const d = await db();
    const row = (await d.get("pages", key)) as CachedPage | undefined;
    if (row) {
      row.hits = (row.hits ?? 0) + 1;
      try { await d.put("pages", row); } catch {}
    }
    return row;
  } catch { return undefined; }
}

export async function cachePut(entry: Omit<CachedPage, "hits" | "bytes"> & { bytes?: number; hits?: number }): Promise<void> {
  try {
    const d = await db();
    const row: CachedPage = {
      key: entry.key, kind: entry.kind, url: entry.url, text: entry.text,
      bytes: entry.bytes ?? entry.text.length,
      fetchedAt: entry.fetchedAt ?? Date.now(),
      hits: entry.hits ?? 0,
    };
    await d.put("pages", row);
  } catch {}
}

export async function cacheList(opts: { kind?: CacheKind; limit?: number } = {}): Promise<CachedPage[]> {
  try {
    const d = await db();
    const all = (await d.getAll("pages")) as CachedPage[];
    const filtered = opts.kind ? all.filter((r) => r.kind === opts.kind) : all;
    filtered.sort((a, b) => b.fetchedAt - a.fetchedAt);
    return opts.limit ? filtered.slice(0, opts.limit) : filtered;
  } catch { return []; }
}

export async function cacheStats(): Promise<{ count: number; bytes: number; oldest: number | null; newest: number | null }> {
  const all = await cacheList();
  if (!all.length) return { count: 0, bytes: 0, oldest: null, newest: null };
  return {
    count: all.length,
    bytes: all.reduce((s, r) => s + r.bytes, 0),
    oldest: Math.min(...all.map((r) => r.fetchedAt)),
    newest: Math.max(...all.map((r) => r.fetchedAt)),
  };
}

export async function cacheDelete(key: string): Promise<void> {
  try { (await db()).delete("pages", key); } catch {}
}
export async function cacheClear(): Promise<void> {
  try { (await db()).clear("pages"); } catch {}
}

/** Default freshness window: 6h. After this we'll prefer a refresh when online,
 *  but the stale entry is still returned offline. */
export const DEFAULT_FRESH_MS = 6 * 60 * 60 * 1000;
export function isFresh(row: CachedPage, ttlMs = DEFAULT_FRESH_MS): boolean {
  return Date.now() - row.fetchedAt < ttlMs;
}

/** Browser storage quota for the whole origin (covers every IndexedDB SQL,
 *  Cache API entries, localStorage etc.) — used to render a "free space" bar. */
export async function storageQuota(): Promise<{ usage: number; quota: number; supported: boolean }> {
  try {
    if (typeof navigator !== "undefined" && navigator.storage?.estimate) {
      const e = await navigator.storage.estimate();
      return { usage: e.usage ?? 0, quota: e.quota ?? 0, supported: true };
    }
  } catch {}
  return { usage: 0, quota: 0, supported: false };
}

export interface CleanupSummary { removed: number; bytesFreed: number; }

/** Delete cached pages older than `maxAgeMs`. */
export async function cacheExpireOlderThan(maxAgeMs: number): Promise<CleanupSummary> {
  const all = await cacheList();
  const cutoff = Date.now() - maxAgeMs;
  const victims = all.filter((p) => p.fetchedAt < cutoff);
  let bytesFreed = 0;
  for (const v of victims) { await cacheDelete(v.key); bytesFreed += v.bytes; }
  return { removed: victims.length, bytesFreed };
}

/** Trim the cache LRU-style until total bytes fit `budgetBytes`. Oldest
 *  fetches die first; entries with high hit-counts are protected. */
export async function cacheTrimToBudget(budgetBytes: number): Promise<CleanupSummary> {
  const all = await cacheList();
  let total = all.reduce((s, p) => s + p.bytes, 0);
  if (total <= budgetBytes) return { removed: 0, bytesFreed: 0 };
  const ordered = all.slice().sort((a, b) => {
    if ((a.hits ?? 0) !== (b.hits ?? 0)) return (a.hits ?? 0) - (b.hits ?? 0);
    return a.fetchedAt - b.fetchedAt;
  });
  let removed = 0, bytesFreed = 0;
  for (const p of ordered) {
    if (total <= budgetBytes) break;
    await cacheDelete(p.key);
    total -= p.bytes; bytesFreed += p.bytes; removed += 1;
  }
  return { removed, bytesFreed };
}

/** Full-text search over the cached page bodies. Returns a snippet around
 *  the first match plus a derived title (first markdown heading or hostname). */
export interface CachedHit {
  page: CachedPage;
  title: string;
  snippet: string;
  matchAt: number;
}
export async function cacheSearch(query: string, limit = 40): Promise<CachedHit[]> {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const all = await cacheList();
  const out: CachedHit[] = [];
  for (const p of all) {
    const hay = p.text.toLowerCase();
    const at = hay.indexOf(q);
    if (at === -1 && !p.url.toLowerCase().includes(q)) continue;
    const start = Math.max(0, (at === -1 ? 0 : at) - 80);
    const end = Math.min(p.text.length, (at === -1 ? 240 : at + q.length + 160));
    out.push({
      page: p,
      title: derivedTitle(p),
      snippet: (start > 0 ? "…" : "") + p.text.slice(start, end).replace(/\s+/g, " ").trim() + (end < p.text.length ? "…" : ""),
      matchAt: at,
    });
    if (out.length >= limit) break;
  }
  return out.sort((a, b) => b.page.fetchedAt - a.page.fetchedAt);
}

export function derivedTitle(p: CachedPage): string {
  const md = p.text.match(/^\s*#\s+(.+?)\s*$/m);
  if (md) return md[1].slice(0, 160);
  const title = p.text.match(/^Title:\s*(.+)$/mi);
  if (title) return title[1].slice(0, 160);
  try { return new URL(p.url).hostname + new URL(p.url).pathname.replace(/\/$/, ""); }
  catch { return p.url; }
}

export function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(2)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
